import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  User,
  ShoppingBag,
  FileText,
  ListMusic,
  Camera,
  ClipboardList,
  CalendarClock,
  CalendarCheck,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  FolderArchive,
} from "lucide-react";
import { meApi } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ClientProgress from "@/components/ClientProgress";

const BASE_TILES = [
  { to: "/espace-client/profil", label: "Détails du compte", desc: "Mettez à jour vos coordonnées", icon: User },
  { to: "/espace-client/commandes", label: "Mes commandes", desc: "Suivez vos demandes de devis", icon: ShoppingBag, badgeKey: "commandes" },
  { to: "/espace-client/documents", label: "Mes documents", desc: "Devis, contrats signés & factures", icon: FolderArchive, badgeKey: "documents", highlightKey: "documents_to_sign" },
  { to: "/espace-client/contrat", label: "Mon Devis · Contrat & Assurance", desc: "Consultez votre contrat", icon: FileText },
  { to: "/espace-client/playlist", label: "Ma Playlist", desc: "Vos titres incontournables", icon: ListMusic, doneKey: "playlist_done" },
  { to: "/espace-client/photobooth", label: "Mon Photobooth", desc: "Vos photos & personnalisation du stand", icon: Camera },
  { to: "/espace-client/planning", label: "Mon planning", desc: "Le déroulé minute par minute", icon: CalendarClock },
  { to: "/espace-client/rdv", label: "Mes RDV à venir", desc: "Préparation, repérage, prestation", icon: CalendarCheck, badgeKey: "rdv" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState({});

  useEffect(() => {
    meApi.summary().then(setSummary).catch(() => {});
  }, []);

  // Build a friendly display name from the user object — never show fragmented email
  const fullName = [user?.prenom, user?.nom].filter(Boolean).join(" ").trim();
  const displayName = fullName || user?.prenom || user?.nom || user?.name || "vous";

  // Compute the conditional questionnaire tile based on client's event type
  const questionnaireTile = summary?.is_mariage
    ? { to: "/espace-client/questionnaire", label: "Questionnaire mariage", desc: "Préparons le grand jour", icon: ClipboardList, doneKey: "questionnaire_done", testid: "ec-questionnaire-mariage" }
    : { to: "/espace-client/questionnaire-prep", label: "Questionnaire de préparation", desc: "Aidez-nous à personnaliser votre événement", icon: ClipboardList, doneKey: "questionnaire_prep_done", testid: "ec-questionnaire-prep" };

  const TILES = [
    ...BASE_TILES.slice(0, 6),
    questionnaireTile,
    ...BASE_TILES.slice(6),
  ];

  return (
    <div data-testid="ec-dashboard">
      <header className="mb-10">
        <p className="eyebrow">Tableau de bord</p>
        <h1 className="font-serif-luxe text-4xl md:text-5xl leading-[1.05] mt-3">
          Bonjour <span className="italic gold-text">{displayName}.</span>
        </h1>
        <p className="text-white/60 text-sm md:text-base mt-3 max-w-2xl">
          Bienvenue dans votre espace personnel My Animation. Préparez votre événement à votre rythme — nous nous occupons du reste.
        </p>
      </header>

      {/* Barre de progression du dossier client */}
      <ClientProgress progression={summary.progression} isMariage={!!summary.is_mariage} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {TILES.map(({ to, label, desc, icon: Icon, badgeKey, doneKey, highlightKey, testid }) => {
          const badge = badgeKey ? summary[badgeKey] : null;
          const done = doneKey ? summary[doneKey] : null;
          const highlight = highlightKey ? summary[highlightKey] : null;
          return (
            <Link
              key={to}
              to={to}
              data-testid={testid || `tile-${to.split("/").pop()}`}
              className="card-luxe p-6 group relative overflow-hidden hover:border-[#DCB457]/45 transition-all duration-300"
            >
              <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-[#DCB457]/0 group-hover:bg-[#DCB457]/10 blur-2xl transition-all" />
              <div className="relative flex items-start justify-between mb-5">
                <div className="w-11 h-11 border border-[#DCB457]/40 flex items-center justify-center text-[#DCB457]">
                  <Icon size={19} strokeWidth={1.2} />
                </div>
                <ArrowUpRight size={16} strokeWidth={1.3} className="text-white/30 group-hover:text-[#DCB457] transition" />
              </div>
              <h3 className="relative font-serif-luxe text-lg md:text-xl leading-snug">{label}</h3>
              <p className="relative text-white/55 text-xs md:text-sm mt-2 leading-relaxed">{desc}</p>
              <div className="relative flex items-center gap-2 mt-4 flex-wrap">
                {typeof badge === "number" && badge > 0 && (
                  <span className="text-[10px] uppercase tracking-[0.2em] bg-[#DCB457] text-black px-2 py-1 font-medium">
                    {badge} {to.endsWith("documents") ? "doc(s)" : "en cours"}
                  </span>
                )}
                {typeof highlight === "number" && highlight > 0 && (
                  <span className="text-[10px] uppercase tracking-[0.2em] bg-amber-500/90 text-black px-2 py-1 font-medium animate-pulse">
                    {highlight} à signer
                  </span>
                )}
                {done === true && (
                  <span className="text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1 text-emerald-300 border border-emerald-400/40 px-2 py-1">
                    <CheckCircle2 size={11} strokeWidth={1.5} /> Renseigné
                  </span>
                )}
                {done === false && (
                  <span className="text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1 text-white/50 border border-white/15 px-2 py-1">
                    <Clock size={11} strokeWidth={1.5} /> À remplir
                  </span>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
